<template>
  <div ref="marqueeContainer" class="marquee-container">
    <div ref="marqueeContent" class="marquee-content min-h-8 bg-green-100">
      <ul ref="marqueeList" class="marquee-list list-none">
        <slot />
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "Marquee",
  props: {
    duration: {
      type: Number,
      default: 120, // This is the animationDuration
    },
    direction: {
      type: String,
      default: "left", // "left" or "right"
    },
    pauseOnHover: {
      type: Boolean,
      default: false, // Pause scrolling when hovering
    },
  },
  mounted() {
    this.initializeMarquee();
  },
  methods: {
    initializeMarquee() {
      const container = this.$refs.marqueeContainer;
      const content = this.$refs.marqueeContent;
      const list = this.$refs.marqueeList;

      // Text content should be hidden by default
      Array.from(list.children).forEach((child) => {
        child.classList.remove("hidden");
      });

      // Pause animation on hover, if enabled
      container.classList.toggle('marquee-container--pause-animation-on-hover', this.pauseOnHover);

      // Duplicate content for seamless scrolling
      const clonedContent = content.cloneNode(true);
      clonedContent.firstChild.setAttribute("aria-hidden", "true");
      content.innerHTML += clonedContent.innerHTML;

      // Apply styles dynamically
      content.style.animationDuration = `${this.duration}s`;
      content.style.animationDirection = this.direction === "right" ? "reverse" : "normal";
    },
    pauseAnimation() {
      this.toggleAnimation(true);
    },
    resumeAnimation() {
      this.toggleAnimation(false);
    },
    toggleAnimation(shouldAnimate) {
      // During initialization, the lists are duplicated to create a seamless and continuous sliding effect for the marquee,
      // so for that reason, we can't just reference the main list element via this.$refs.marqueeList and instead we need to
      // apply our changes to all duplicated lists.
      const marqueeListElements = this.$refs.marqueeContent.children;
      for (const marqueeListElement of marqueeListElements) {
        marqueeListElement.classList.toggle('paused', shouldAnimate);
      }
    }
  },
};
</script>

<style scoped>
.marquee-container {
  @apply relative overflow-hidden whitespace-nowrap w-full mb-4;
  height: auto;
}

.marquee-content {
  --gap: 5rem;
  @apply flex overflow-hidden items-center;
  user-select: none;
  gap: var(--gap);
}

.marquee-list {
  @apply flex-shrink-0 flex justify-around min-w-full text-green-800;
  gap: var(--gap);
  animation: scroll linear infinite;
  animation-duration: inherit;

  animation-play-state: running;
  -o-animation-play-state: running;
  -moz-animation-play-state: running;
  -webkit-animation-play-state: running;
}

/* Apply the pause effect on the marquee-list when the marquee-container is hovered */
.marquee-container--pause-animation-on-hover:hover .marquee-list {
  animation-play-state: paused !important;
  -o-animation-play-state: paused !important;
  -moz-animation-play-state: paused !important;
  -webkit-animation-play-state: paused !important;
}

@keyframes scroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(-100% - var(--gap)));
  }
}
</style>
